import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  Briefcase,
  Database,
  Zap,
  Mic,
  Share2,
  ScanLine,
  Sliders,
  Crown,
  Search,
  ExternalLink,
  Globe,
  Coffee,
  ShieldCheck,
  Lock,
} from 'lucide-react';
import type { AdminUser } from '../../lib/api';

interface AdminNavProps {
  currentUser: AdminUser | null;
  activeAlertsCount?: number;
  onNavigate?: () => void;
}

interface SidebarNavItem {
  id: string;
  path: string;
  label: string;
  subtitle: string;
  badge?: string;
  badgeClass?: string;
  icon: React.ComponentType<{ className?: string }>;
  externalKiosk?: boolean;
  readOnlyForOps?: boolean;
  showAlertCount?: boolean;
  isActive: (pathname: string) => boolean;
}

interface SidebarPillar {
  id: string;
  number: string;
  title: string;
  allowedRoles: string[];
  items: SidebarNavItem[];
}

export function AdminNav({ currentUser, activeAlertsCount = 0, onNavigate }: AdminNavProps) {
  const location = useLocation();
  const [filterQuery, setFilterQuery] = useState('');

  // Default to owner/super_admin visibility if currentUser hasn't resolved yet (e.g. break-glass key)
  const effectiveRole: string = (currentUser?.role as string) || 'super_admin';
  const normalizedRole: string = effectiveRole === 'staff' ? 'gate' : effectiveRole;

  const pillars: SidebarPillar[] = [
    {
      id: 'commercial',
      number: 'Pillar 1',
      title: 'Commercial & Sales',
      allowedRoles: ['super_admin', 'owner', 'operations'],
      items: [
        {
          id: 'sales',
          path: '/admin/sales',
          label: 'Sales & Stalls',
          subtitle: 'Booths, offline bookings & passes',
          badge: normalizedRole === 'operations' ? 'Read-Only' : 'Live',
          badgeClass:
            normalizedRole === 'operations'
              ? 'bg-slate-200 text-slate-700'
              : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
          icon: Briefcase,
          readOnlyForOps: normalizedRole === 'operations',
          isActive: (p) => p === '/admin' || p === '/admin/' || p.startsWith('/admin/sales'),
        },
      ],
    },
    {
      id: 'crm-marketing',
      number: 'Pillar 2',
      title: 'CRM, Curation & Marketing',
      allowedRoles: ['super_admin', 'owner'],
      items: [
        {
          id: 'crm',
          path: '/admin/crm',
          label: 'CRM & Applications',
          subtitle: 'B2B pipeline, pods & press inbox',
          icon: Database,
          isActive: (p) => p.startsWith('/admin/crm'),
        },
        {
          id: 'speakers',
          path: '/admin/speakers',
          label: 'Speakers & Agenda',
          subtitle: 'Proposals, slide decks & stage matrix',
          icon: Mic,
          isActive: (p) => p.startsWith('/admin/speakers'),
        },
        {
          id: 'automations',
          path: '/admin/automations',
          label: 'Campaigns & Workflows',
          subtitle: 'System health, recovery & broadcasts',
          icon: Zap,
          showAlertCount: true,
          isActive: (p) => p.startsWith('/admin/automations') || p.startsWith('/admin/automate'),
        },
        {
          id: 'social',
          path: '/admin/social',
          label: 'Social Studio',
          subtitle: 'Multi-platform content calendar',
          icon: Share2,
          isActive: (p) => p.startsWith('/admin/social'),
        },
      ],
    },
    {
      id: 'live-ops',
      number: 'Pillar 3',
      title: 'Event-Day Live Ops',
      allowedRoles: ['super_admin', 'owner', 'operations', 'desk', 'gate'],
      items: [
        {
          id: 'operations',
          path: '/admin/operations',
          label: 'Live Ops & Helpdesk',
          subtitle: 'Check-in, badge print & analytics',
          badge: 'JWCC Pav 3',
          badgeClass: 'bg-[#C5A059]/20 text-[#E6C687] border border-[#C5A059]/40',
          icon: ScanLine,
          isActive: (p) => p.startsWith('/admin/operations'),
        },
        {
          id: 'kiosk-checkin',
          path: '/checkin',
          label: 'Gate Turnstile Scanner ↗',
          subtitle: 'Fullscreen QR admission kiosk',
          icon: ScanLine,
          externalKiosk: true,
          isActive: (p) => p.startsWith('/checkin'),
        },
        {
          id: 'kiosk-perks',
          path: '/counter/perks',
          label: 'Perks & Lunch Counter ↗',
          subtitle: 'Tea, lunch & kit redemption kiosk',
          icon: Coffee,
          externalKiosk: true,
          isActive: (p) => p.startsWith('/counter/perks') || p.startsWith('/vendor/perks'),
        },
      ],
    },
    {
      id: 'governance',
      number: 'Pillar 4',
      title: 'System & Governance',
      allowedRoles: ['super_admin', 'owner'],
      items: [
        {
          id: 'configure',
          path: '/admin/configure',
          label: 'Floor Plan & Pricing',
          subtitle: 'SVG map builder, stall & pass rates',
          icon: Sliders,
          isActive: (p) => p.startsWith('/admin/configure'),
        },
        {
          id: 'team',
          path: '/admin/team',
          label: 'Team & Role Access',
          subtitle: '5-tier staff RBAC & permissions',
          icon: Crown,
          isActive: (p) => p.startsWith('/admin/team'),
        },
      ],
    },
  ];

  const q = filterQuery.trim().toLowerCase();

  const visiblePillars = pillars
    .filter((pillar) => pillar.allowedRoles.includes(normalizedRole))
    .map((pillar) => ({
      ...pillar,
      items: pillar.items.filter(
        (item) =>
          !q ||
          item.label.toLowerCase().includes(q) ||
          item.subtitle.toLowerCase().includes(q) ||
          pillar.title.toLowerCase().includes(q)
      ),
    }))
    .filter((pillar) => pillar.items.length > 0);

  return (
    <aside
      aria-label="Admin Workspace Sidebar"
      className="w-full lg:w-72 bg-[#062B1B] text-white flex flex-col justify-between shrink-0 border-r border-[#0A3D27] select-none"
    >
      {/* Top Brand & Quick Filter */}
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-2 pb-3 border-b border-emerald-900/80">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-[#0A3D27] border border-emerald-700/60 flex items-center justify-center shadow-xs shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#E6C687]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-black text-sm tracking-tight text-white truncate">
                  IIAB 2027 Console
                </span>
              </div>
              <span className="block text-[10px] text-emerald-300/80 font-semibold truncate">
                Staff &amp; Operations Workspace
              </span>
            </div>
          </div>
        </div>

        {/* Quick Search / Filter */}
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-emerald-400/70 absolute left-3 top-2.5" />
          <input
            type="text"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            placeholder="Filter console modules..."
            className="w-full bg-[#0A3D27]/70 border border-emerald-800/80 rounded-xl pl-8 pr-3 py-1.5 text-xs text-white placeholder-emerald-300/50 focus:outline-hidden focus:border-[#E6C687]"
          />
        </div>

        {/* 4-Pillar Navigation List */}
        <nav className="space-y-5 overflow-y-auto max-h-[calc(100vh-240px)] pr-1">
          {visiblePillars.map((pillar) => (
            <div key={pillar.id} className="space-y-1.5">
              <div className="px-2 flex items-center justify-between text-[10px] font-black uppercase tracking-wider text-emerald-400/80">
                <span>
                  {pillar.number}: {pillar.title}
                </span>
              </div>

              <div className="space-y-1">
                {pillar.items.map((item) => {
                  const active = item.isActive(location.pathname);
                  const IconComponent = item.icon;

                  if (item.externalKiosk) {
                    return (
                      <Link
                        key={item.id}
                        to={item.path}
                        onClick={onNavigate}
                        className="group flex items-center justify-between gap-2.5 px-3 py-2 rounded-xl text-left transition-all text-emerald-100/80 hover:text-white hover:bg-[#0A3D27]/80 border border-transparent hover:border-emerald-700/60"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-lg bg-emerald-950/80 text-emerald-300 flex items-center justify-center shrink-0 group-hover:bg-emerald-800/60">
                            <IconComponent className="w-3.5 h-3.5" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-bold truncate flex items-center gap-1">
                              <span>{item.label}</span>
                            </div>
                            <p className="text-[10px] text-emerald-300/65 truncate">{item.subtitle}</p>
                          </div>
                        </div>
                        <ExternalLink className="w-3 h-3 text-emerald-400/60 shrink-0" />
                      </Link>
                    );
                  }

                  return (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      onClick={onNavigate}
                      className={`group flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-left transition-all border ${
                        active
                          ? 'bg-[#FAF5EA] text-slate-900 border-[#E6C687] shadow-sm'
                          : 'text-emerald-100/90 hover:text-white hover:bg-[#0A3D27] border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                            active
                              ? 'bg-[#0A3D27] text-[#FAF5EA]'
                              : 'bg-[#0A3D27]/90 text-emerald-200 group-hover:bg-emerald-800/80'
                          }`}
                        >
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-extrabold flex items-center gap-1.5 flex-wrap">
                            <span className="truncate">{item.label}</span>
                            {item.readOnlyForOps && (
                              <span title="Read-Only Access" className="inline-flex">
                                <Lock className="w-3 h-3 text-amber-400 inline shrink-0" />
                              </span>
                            )}
                          </div>
                          <p
                            className={`text-[10px] truncate ${
                              active ? 'text-slate-600 font-medium' : 'text-emerald-300/70'
                            }`}
                          >
                            {item.subtitle}
                          </p>
                        </div>
                      </div>

                      {item.showAlertCount && activeAlertsCount > 0 ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-600 text-white shrink-0 animate-pulse">
                          {activeAlertsCount}
                        </span>
                      ) : item.badge ? (
                        <span
                          className={`px-2 py-0.5 rounded-full text-[9px] font-bold shrink-0 ${
                            active
                              ? 'bg-[#0A3D27]/15 text-[#0A3D27] border border-[#0A3D27]/20'
                              : item.badgeClass || 'bg-emerald-800 text-emerald-100'
                          }`}
                        >
                          {item.badge}
                        </span>
                      ) : null}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Bottom Public Website Link */}
      <div className="p-4 border-t border-emerald-900/80 bg-[#041E13] space-y-2">
        <Link
          to="/"
          className="flex items-center justify-between px-3 py-2 rounded-xl bg-[#0A3D27]/70 hover:bg-[#0A3D27] text-emerald-100 hover:text-white text-xs font-bold transition-colors border border-emerald-800/60"
        >
          <span className="flex items-center gap-2">
            <Globe className="w-3.5 h-3.5 text-[#E6C687]" />
            <span>Back to Public Website</span>
          </span>
          <ExternalLink className="w-3 h-3 text-emerald-300" />
        </Link>
      </div>
    </aside>
  );
}

export default AdminNav;
